import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Heart, Star, ShoppingCart } from 'lucide-react';
import { cn } from '../utils/cn';

export interface Product {
  id: string | number;
  title: string;
  price: number;
  originalPrice?: number;
  currency?: string;
  image?: string;
  /** Optional gallery of additional image URLs (the hero stays on `image`). */
  images?: string[];
  imageBg?: string;
  category?: string;
  rating?: number;
  reviews?: number;
  badge?: string;
  /**
   * GST rate (%) for this product. Carried, never rendered by the card.
   *
   * The cart stores whole `Product` objects, so anything checkout needs
   * at invoice time has to survive on this interface — a `ShopProduct`
   * widens to `Product` on `add()`, and a field missing here is a field
   * checkout cannot read. Its absence is why every storefront invoice
   * billed a flat 18% regardless of the product's configured rate.
   */
  taxRate?: number;
}

export interface ProductCardProps {
  product: Product;
  onClick?: (product: Product) => void;
  onAddToCart?: (product: Product) => void;
  /**
   * When provided, a heart-icon button is rendered in the
   * top-right corner of the image. Click toggles the wishlist
   * state — the consumer is responsible for the actual storage
   * via `useWishlist()` (or eventually `wishlistService.toggle`).
   */
  onToggleWishlist?: (product: Product) => void;
  /** Drives the heart's filled / outline appearance. */
  isWishlisted?: boolean;
  className?: string;
  compact?: boolean;
  cta?: ReactNode;
}

export function ProductCard({
  product,
  onClick,
  onAddToCart,
  onToggleWishlist,
  isWishlisted,
  className,
  compact = false,
  cta,
}: ProductCardProps) {
  const currency = product.currency ?? '₹';
  const discount =
    product.originalPrice && product.originalPrice > product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0;

  /* Skeleton shimmer until the image (a) loads or (b) errors, plus
     onError → neutral KSS "K" monogram fallback (Fix 12). The brand
     placeholder reuses the existing primary-* tokens so it sits in
     the theme without adding new assets. */
  const [imageState, setImageState] = useState<'loading' | 'ready' | 'error'>(
    product.image ? 'loading' : 'error',
  );

  const handleLoad = () => setImageState('ready');
  const handleError = (_e: SyntheticEvent<HTMLImageElement>) =>
    setImageState('error');

  return (
    <div
      className={cn(
        /* Sojanya-style fashion card (2026-06-11):
           - no border / no shadow at rest — image and content sit on
             one continuous surface so the image truly dominates
           - rounded corners on the OUTER element only; inner image
             gets `rounded-t-lg` via overflow-hidden
           - hover lifts the card a bit (shadow) and reveals the
             floating cart icon */
        'group flex flex-col overflow-hidden rounded-lg bg-surface-card transition-shadow hover:shadow-md',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={() => onClick?.(product)}
    >
      <div
        className={cn(
          'relative flex w-full items-center justify-center overflow-hidden',
          /* Image now uses `aspect-[3/4]` (taller portrait) so it
             owns ~75% of the card height — closer to the editorial
             fashion-grid look at sojanya.com. The content footer
             below renders at its intrinsic height. */
          compact ? 'h-28' : 'aspect-[3/4]',
        )}
        style={{ background: product.imageBg || 'var(--color-bg-card-hover)' }}
      >
        {product.image && imageState !== 'error' ? (
          <>
            {imageState === 'loading' && (
              <span
                aria-hidden
                className="absolute inset-0 animate-pulse bg-[var(--color-bg-card-hover)]"
              />
            )}
            <img
              src={product.image}
              alt={product.title}
              onLoad={handleLoad}
              onError={handleError}
              className={cn(
                'h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]',
                imageState === 'loading' && 'opacity-0',
              )}
            />
          </>
        ) : (
          // Brand-mark fallback — used both when there's no `image`
          // and when the image url fails. Never expose a broken-image
          // icon or raw alt text to shoppers.
          <span
            aria-label="Image unavailable"
            className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-[var(--color-primary)] text-base font-bold text-[var(--color-primary-fg)]"
          >
            K
          </span>
        )}
        {product.badge && (
          /* Sale / featured tag — rectangular, no rounded-full, sits
             top-LEFT as on Sojanya. Tighter typography. */
          <span className="absolute left-0 top-3 bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
            {product.badge}
          </span>
        )}
        {onToggleWishlist && (
          <button
            type="button"
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Save to wishlist'}
            onClick={(e) => {
              // Stop the card click handler from firing (otherwise
              // tapping the heart also navigates to the PDP).
              e.stopPropagation();
              onToggleWishlist(product);
            }}
            className={cn(
              'absolute right-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-card/90 shadow-sm backdrop-blur transition-colors hover:bg-surface-card',
              isWishlisted ? 'text-danger' : 'text-content-secondary',
            )}
          >
            <Heart className={cn('h-4 w-4', isWishlisted && 'fill-current')} />
          </button>
        )}
        {discount > 0 && (
          /* Discount chip — sits BELOW the badge column on the left
             so it stacks naturally with a SALE tag. */
          <span
            className={cn(
              'absolute left-0 bg-danger px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white',
              product.badge ? 'top-9' : 'top-3',
            )}
          >
            -{discount}%
          </span>
        )}

        {/* Floating Add-to-cart icon — circular white pill, bottom-
            right of the image. Always visible on touch / coarse-
            pointer devices; on desktop it fades in on card hover
            (the `group-hover` utility). Replaces the text "Add"
            button per user request 2026-06-11. */}
        {cta === undefined && onAddToCart && !compact && (
          <button
            type="button"
            aria-label="Add to cart"
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product);
            }}
            className={cn(
              'absolute bottom-3 right-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-surface-card text-primary shadow-md transition-all hover:bg-primary hover:text-primary-foreground',
              /* Mouse / fine-pointer users see the icon only on
                 hover so the image stays uncluttered; touch users
                 always see it (no hover state to rely on). */
              'opacity-100 sm:opacity-0 sm:group-hover:opacity-100',
            )}
          >
            <ShoppingCart className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Content footer — clean two-row layout: category + title on
          row one, price row on row two. No inline Add button (it's
          floating on the image above). */}
      <div className={cn('flex min-w-0 flex-1 flex-col gap-1', compact ? 'p-2.5' : 'px-3.5 pb-3 pt-2.5')}>
        {product.category && (
          <div className="text-[10px] font-medium uppercase tracking-wider text-content-muted">
            {product.category}
          </div>
        )}
        <div className="line-clamp-1 text-sm font-medium text-content-primary">
          {product.title}
        </div>

        {typeof product.rating === 'number' && (
          <div className="flex items-center gap-1 text-xs text-content-muted">
            <Star className="h-3 w-3 fill-warning text-warning" />
            <span className="font-medium text-content-primary">{product.rating.toFixed(1)}</span>
            {product.reviews != null && <span>({product.reviews})</span>}
          </div>
        )}

        <div className="mt-auto flex min-w-0 flex-wrap items-baseline gap-2 pt-1">
          <span className="truncate text-base font-bold text-content-primary">
            {currency}
            {product.price.toLocaleString('en-IN')}
          </span>
          {product.originalPrice && product.originalPrice > product.price && (
            <span className="truncate text-xs text-content-muted line-through">
              {currency}
              {product.originalPrice.toLocaleString('en-IN')}
            </span>
          )}
          {/* Custom `cta` slot — if a caller passes a `cta` prop,
              render it inline alongside the price. Default cards now
              use the floating cart icon above; this keeps the PDP
              "Related products" rail and any future variant intact. */}
          {cta && (
            <div className="ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
              {cta}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
